import keycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import { httpClientAuthorizer } from "@sia/style-guide";

const masterKeyUrl = `${process.env.REACT_APP_BENCHMARKING_SERVICE_URL}/masterkey_hash`;
const responseOf = (error) => error?.response || (Number.isInteger(error?.status) ? error : null);
const hasExplicitValidity = (response) => typeof response?.data?.valid === "boolean";
const isSuccessfulStatus = (status) => Number.isInteger(status) && status >= 200 && status < 300;
const isGeneratedValidationHash = (hash) => /^v2:[0-9a-f]{64}$/i.test(hash) || /^[0-9a-f]{32}$/i.test(hash);
const isInvalidCandidateResponse = (response, hash) =>
  response?.status === 400 &&
  (response?.data?.valid === false || isGeneratedValidationHash(hash));
const annotateError = (error, details) => {
  const wrapped = new Error(error?.message || "Master-key verifier request failed");
  wrapped.cause = error;
  return Object.assign(wrapped, { originalError: error, ...details });
};
const persistenceError = (error, details = {}) => {
  const requestSent = error?.requestSent !== false;
  return annotateError(error, {
    requestSent,
    safeToContinue: !requestSent,
    requiresRecovery: requestSent,
    persistenceState: requestSent ? "unknown" : "source",
    failureStage: "verifier-update",
    ...details,
  });
};

export default class MasterKeyVerifierApi {
  constructor(httpClient) {
    this.httpClient = httpClient;
  }

  async request(options) {
    let config;
    try {
      await keycloakIDPAdapter.getClient().updateToken(0);
      config = httpClientAuthorizer.authorizeRequestConfig(keycloakIDPAdapter, { headers: {} });
    } catch (error) {
      throw annotateError(error, {
        requestSent: false,
        safeToContinue: true,
        requiresRecovery: false,
        persistenceState: "source",
      });
    }
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (callback) => (value) => {
        if (settled) return;
        settled = true;
        callback(value);
      };
      const succeed = settle(resolve);
      const fail = settle(reject);
      try {
        const pending = this.httpClient.request({ ...options, ignoreError: true, config, onSuccess: succeed, onError: fail });
        pending?.catch?.(fail);
      } catch (error) {
        fail(error);
      }
    });
  }

  async validateHash(hash) {
    let response;
    try {
      response = await this.request({
        method: "GET",
        url: `${masterKeyUrl}/valid?masterkey_hash=${encodeURIComponent(hash)}`,
      });
    } catch (error) {
      const candidate = responseOf(error);
      if (!isInvalidCandidateResponse(candidate, hash)) throw error;
      response = candidate;
    }
    if (isInvalidCandidateResponse(response, hash)) {
      return { valid: false, response: { ...response, data: { ...(response?.data || {}), valid: false } } };
    }
    if (response?.status !== 200 || !hasExplicitValidity(response)) throw new Error("Master-key validation returned an invalid response");
    return { valid: response.data.valid, response };
  }

  async exists() {
    const response = await this.request({ method: "GET", url: `${masterKeyUrl}/exist` });
    if (response?.status !== 200 || !hasExplicitValidity(response)) throw new Error("Master-key existence check returned an invalid response");
    return response;
  }

  async persistHash(endpoint, hash) {
    let response;
    try {
      response = await this.request({
        method: "POST",
        url: `${masterKeyUrl}/${endpoint}`,
        data: { masterkey_hash: hash },
      });
    } catch (error) {
      throw persistenceError(error);
    }
    if (isSuccessfulStatus(response?.status)) return response;
    throw persistenceError(new Error(`Master-key ${endpoint} returned HTTP ${response?.status ?? "unknown"}`), {
      response,
      requestSent: true,
    });
  }

  addHash(hash) {
    return this.persistHash("add", hash);
  }

  changeHash(hash) {
    return this.persistHash("change", hash);
  }

  resetHash(hash) {
    return this.persistHash("reset", hash);
  }
}
